import React from 'react';
import HeroSection from '../components/Hero/HeroSection';
import ServicesSection from '../components/Services/ServicesSection';
import PerformanceSection from '../components/Performance/PerformanceSection';
import CompanyStorySection from '../components/About/CompanyStorySection';
import PhilosophySection from '../components/About/PhilosophySection';
import LeadershipSection from '../components/About/LeadershipSection';
import { useScrollReveal } from '../hooks/useScrollReveal';
import type { Service } from '../components/Services/ServicesSection';
import './Pages.css';
import './MainPage.css';

const MainPage: React.FC = () => {
  const advantageRefs = [
    useScrollReveal<HTMLDivElement>(),
    useScrollReveal<HTMLDivElement>(),
    useScrollReveal<HTMLDivElement>(),
    useScrollReveal<HTMLDivElement>(),
  ];

  // ===== HOME SECTION DATA =====
  const heroStats = [
    { value: '2,500+', label: 'Stocks Analyzed' },
    { value: '28+', label: 'Financial Ratios' }
  ];

  const services: Service[] = [
    {
      icon: '🎯',
      title: 'Quantitative Screening',
      description: 'Proprietary stock selection framework built on financial ratios and valuation metrics with proven predictive power across market cycles.',
      className: 'stagger-1'
    },
    {
      icon: '📊',
      title: 'Deep Fundamental Analysis',
      description: 'Rigorous deep-dive company analysis examining financial health, earnings quality, and long-term value creation potential.',
      className: 'stagger-2'
    },
    {
      icon: '🤖',
      title: 'AI-Enhanced Selection',
      description: 'Machine learning algorithms identify hidden market inefficiencies and high-probability investment opportunities before widespread recognition.',
      className: 'stagger-3'
    },
    {
      icon: '⚖️',
      title: 'Portfolio Optimization',
      description: 'Strategic stock weightings optimized based on risk contribution, correlation analysis, and alpha generation for maximum risk-adjusted returns.',
      className: 'stagger-4'
    }
  ];

  const performanceHighlights = [
    { number: '7-Step', text: 'Systematic investment process' },
    { number: '200', text: 'Elite stocks from 2,500+ universe' },
    { number: '28+', text: 'Financial ratios analyzed' }
  ];

  // ===== STORY SECTION DATA =====
  const storyParagraphs = [
    "Third Locust Capital Management was founded on a fundamental belief that systematic, research-driven investment approaches can consistently outperform traditional investment strategies. Our founders, seasoned quantitative finance professionals, recognized a critical gap in the market: while most managers claim sophistication, few employ truly systematic methodologies that eliminate emotional bias and deliver consistent alpha generation.",
    "The genesis of Third Locust came from extensive research demonstrating that manager underperformance stems primarily from three critical flaws: lack of systematic screening processes, inadequate portfolio construction methodologies, and misaligned incentive structures. We set out to build a firm that would address each of these fundamental issues through disciplined application of proven quantitative techniques.",
    "Our name, 'Third Locust,' reflects our position as part of a new generation of systematic investment managers who prioritize empirical evidence over market intuition. Like the biblical locusts, we systematically identify and harvest value from market inefficiencies, but we do so through rigorous analysis rather than destructive speculation.",
    "From inception, Third Locust has been committed to transparency, intellectual honesty, and unwavering adherence to our investment discipline. We believe that superior long-term performance comes not from market timing or speculative positions, but from the consistent application of proven systematic methodologies across all market conditions.",
    "Today, Third Locust manages institutional capital through our proprietary investment process, which combines advanced quantitative screening with fundamental analysis to identify undervalued securities with superior risk-adjusted return potential. Our track record demonstrates the power of systematic investing applied with intellectual rigor and operational discipline."
  ];

  const storyStats = [
    { number: "Systematic", text: "Investment Process" },
    { number: "Quantitative", text: "Screening Approach" },
    { number: "Fundamental", text: "Analysis Integration" },
    { number: "Concentrated", text: "Portfolio Strategy" },
    { number: "Risk-Managed", text: "Construction Method" },
    { number: "Disciplined", text: "Rebalancing Process" }
  ];

  const philosophyItems = [
    {
      id: "systematic",
      icon: "🎯",
      title: "Systematic Discipline",
      description: "We maintain unwavering adherence to our proprietary 7-step investment process, systematically eliminating emotional bias and ensuring consistent application of our proven methodology across all market conditions and cycles."
    },
    {
      id: "quantitative",
      icon: "📊",
      title: "Quantitative Excellence",
      description: "Our investment decisions are grounded in rigorous quantitative analysis utilizing advanced screening methodologies to identify companies with superior fundamental characteristics and compelling valuations across our investment universe."
    },
    {
      id: "risk-management",
      icon: "🛡️",
      title: "Risk-First Framework",
      description: "We employ a comprehensive risk management approach featuring sector diversification limits, position sizing controls, and systematic rebalancing to protect capital while optimizing risk-adjusted returns across our concentrated portfolio."
    },
    {
      id: "ai",
      icon: "🤖",
      title: "AI-Enhanced Analytics",
      description: "Advanced machine learning algorithms augment our fundamental analysis, identifying complex patterns in financial data and optimizing portfolio construction to capitalize on market inefficiencies invisible to traditional analysis."
    },
    {
      id: "transparency",
      icon: "🔍",
      title: "Transparency & Accountability",
      description: "We maintain complete transparency in our investment process and performance reporting, providing detailed attribution analysis and clear communication of our methodology, risks, and results to all stakeholders."
    },
    {
      id: "performance",
      icon: "📈",
      title: "Alpha Generation Focus",
      description: "Our systematic approach is designed specifically for consistent alpha generation, focusing on identifying undervalued securities with superior risk-adjusted return potential rather than chasing short-term market trends or timing strategies."
    }
  ];

  // ===== TEAM SECTION DATA =====
  const leaders = [
    {
      id: "gm",
      name: "Greg McGreevey",
      title: "Managing Partner and Co-Chief Investment Officer",
      bio: "Greg McGreevey is a performance-driven investor and enterprise builder who has turned underperforming platforms into consistent winners across cycles. A veteran operator of global investment businesses at Invesco, Hartford (HIMCO), and ING, he has led firm-wide turnarounds in fixed income and equities, launched and scaled private-markets strategies, and overseen multi-country teams, product, and enterprise risk. Greg blends rigorous fundamental research with systematic discipline-clear research funnels, tight portfolio construction, and relentless post-trade review-to compound capital while controlling drawdowns and taxes. Previously Senior Managing Director at a $1.5T firm, he also ran one of the market's stronger actively-managed equity sleeves by pairing high-conviction security selection with factor and regime analytics. Today he serves as an Independent Trustee of Putnam Investment Management and has served on the board of Invesco Mortgage Capital-experience that reinforces Third Locust's fiduciary, outcome-focused culture. A Chartered Financial Analyst with an MBA (High Distinction), Greg brings a proven playbook for aligning strategy, risk management, and execution-turning complex, multi-asset opportunity sets into clear, repeatable edge and durable, tax-efficient returns.",
      credentials: ["Founding Partner", "Chartered Financial Analyst"],
      initials: "GM",
      photo: "/headshots/gregMC.jpg"
    },
    {
      id: "gs",
      name: "Greg Stoeckle",
      title: "Co-Chief Investment Officer",
      bio: "Greg Stoeckle serves as Co-Chief Investment Officer at Third Locust, bringing extensive experience in portfolio management and investment strategy to the firm's systematic approach.",
      credentials: ["Founding Partner"],
      initials: "GS"
    },
    {
      id: "dh",
      name: "Doug Hutchens",
      title: "Chief Technology Officer",
      bio: "Doug Hutchens leads Third Locust's fundamental analysis and company evaluation process, combining deep expertise in valuation methodologies with a distinguished 26-year career at Microsoft. During his tenure, he built and scaled enterprise software systems across Windows and Azure Virtual Desktop, developing extensive expertise in software engineering, data analysis, machine learning model development, and system debugging. This technical foundation enabled him to create the advanced screening algorithms and automated research tools that power Third Locust's investment framework. Doug's expertise spans data pipeline architecture, predictive modeling, and algorithmic debugging, allowing him to build sophisticated quantitative models that identify investment opportunities with exceptional precision. His unique ability to bridge technology and finance-leveraging cutting-edge machine learning techniques and robust data infrastructure-drives the analytical depth and scalability that defines the firm's research stock selection processes, tooling, and cloud architecture.",
      credentials: ["Founding Partner", "Computer Science Engineer"],
      initials: "DH",
      photo: "/headshots/dougHu.png"
    }
  ];

  // ===== FAQ SECTION DATA =====
  const faqs = [
    {
      question: "Do you time the market?",
      answer: "No. We focus on stock selection and position sizing based on company-specific analysis. While we may adjust risk budgets based on market conditions, our edge comes from identifying undervalued individual securities rather than making broad market timing calls."
    },
    {
      question: "Why focus on small and mid-cap companies?",
      answer: "Small and mid-cap markets offer greater alpha opportunities due to less analyst coverage and market inefficiencies. This is where our systematic screening and research advantages can identify the most compelling investment opportunities."
    },
    {
      question: "How is this different from factor investing?",
      answer: "While factor strategies focus on broad market exposures, we concentrate on company-specific opportunities. We're paid for identifying individual businesses with superior characteristics rather than riding broad style factors that can become crowded and commoditized."
    },
    {
      question: "How do you ensure research quality and avoid data mining?",
      answer: "We use time-correct data, walk-forward validation, and maintain simple, interpretable models. Our research process includes uncertainty analysis and regime-aware considerations to ensure robust, reliable results."
    },
    {
      question: "What happens when an investment thesis changes?",
      answer: "We have systematic monitoring processes with predefined triggers that guide our response: trim, hedge, exit, or re-evaluate positions as needed. We track and measure our speed and effectiveness in managing changing investment scenarios."
    },
    {
      question: "What makes Third Locust different from other investment managers?",
      answer: "Our systematic approach combines rigorous quantitative screening with fundamental analysis to generate returns driven by company-specific insights rather than market timing or style factors. We emphasize transparency, repeatability, and risk-adjusted performance."
    },
    {
      question: "How do you manage risk in the portfolio?",
      answer: "We employ a multi-layered approach including position sizing based on conviction levels, sector diversification limits, and systematic monitoring of portfolio-level risk metrics. Our process is designed to protect capital while optimizing return opportunities."
    },
    {
      question: "What level of transparency can investors expect?",
      answer: "We provide comprehensive reporting including performance attribution, process documentation, and detailed explanations of our investment decisions. Our systematic approach enables clear communication of how decisions translate into returns."
    }
  ];

  return (
    <div className="main-page">
      {/* ===== HOME SECTION ===== */}
      <section id="home" className="page-section">
        <HeroSection
          eyebrow="Systematic Investment Management"
          title="Investment excellence,"
          highlight="built on discipline."
          subtitle="Third Locust Capital Management generates superior risk-adjusted returns through a proprietary 7-step investment process, combining quantitative screening, deep fundamental analysis, and AI-enhanced portfolio optimization."
          processLabel="Step Disciplined Investment Process"
          processSteps={7}
          stats={heroStats}
          showChart={true}
        />

        <ServicesSection
          eyebrow="The Method"
          title="Our Investment Process"
          subtitle="A systematic approach to identifying high-alpha opportunities in Russell 2000 & S&P 500 stocks ($200M - $10B market cap)"
          services={services}
          stepped
        />

        <PerformanceSection
          eyebrow="By The Numbers"
          title="Key Investment Metrics"
          subtitle="Our systematic approach to identifying elite investment opportunities with rigorous quantitative analysis."
          highlights={performanceHighlights}
        />
      </section>

      {/* ===== STORY SECTION ===== */}
      <section id="story" className="page-section">
        <CompanyStorySection
          eyebrow="Our Story"
          title="Building a Better Investment Manager"
          paragraphs={storyParagraphs}
          stats={storyStats}
        />

        <div className="competitive-advantages">
          <div className="section-container">
            <div className="advantages-content">
              <div className="eyebrow centered">Why Third Locust</div>
              <h2>Competitive Advantages</h2>
              <div className="advantages-list">
                <div className="advantage-row" ref={advantageRefs[0]}>
                  <div className="advantage-row-heading">
                    <span className="advantage-row-mark" aria-hidden="true" />
                    <h3>Systematic Alpha Generation</h3>
                  </div>
                  <p>
                    Our proven methodology consistently identifies undervalued securities with superior risk-adjusted
                    return potential, generating alpha through systematic application of quantitative techniques rather
                    than relying on market timing or speculative positions.
                  </p>
                </div>

                <div className="advantage-row" ref={advantageRefs[1]}>
                  <div className="advantage-row-heading">
                    <span className="advantage-row-mark" aria-hidden="true" />
                    <h3>Advanced Technology Stack</h3>
                  </div>
                  <p>
                    We leverage cutting-edge financial technology including machine learning algorithms, advanced
                    analytics platforms, and sophisticated risk management systems to maintain our competitive edge
                    in quantitative analysis and portfolio optimization.
                  </p>
                </div>

                <div className="advantage-row" ref={advantageRefs[2]}>
                  <div className="advantage-row-heading">
                    <span className="advantage-row-mark" aria-hidden="true" />
                    <h3>Institutional-Quality Operations</h3>
                  </div>
                  <p>
                    Third Locust maintains institutional-grade operational infrastructure including prime brokerage
                    relationships, professional service providers, and comprehensive compliance frameworks designed
                    to support sustainable growth and institutional investor requirements.
                  </p>
                </div>

                <div className="advantage-row" ref={advantageRefs[3]}>
                  <div className="advantage-row-heading">
                    <span className="advantage-row-mark" aria-hidden="true" />
                    <h3>Aligned Incentive Structure</h3>
                  </div>
                  <p>
                    Our fee structure and governance model align manager incentives with investor outcomes,
                    emphasizing long-term performance and capital preservation rather than asset gathering
                    or short-term performance chasing.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <PhilosophySection
          eyebrow="Our Principles"
          title="Investment Philosophy & Core Principles"
          subtitle="Third Locust employs a systematic, research-driven investment process that combines quantitative screening with fundamental analysis to identify exceptional opportunities while maintaining rigorous risk management standards."
          philosophyItems={philosophyItems}
        />
      </section>

      {/* ===== TEAM SECTION ===== */}
      <section id="team" className="page-section">
        <LeadershipSection
          eyebrow="Leadership"
          title="Meet Our Leaders"
          subtitle="The founding team behind Third Locust's proven investment methodology, combining deep expertise in quantitative finance, fundamental analysis, and technology."
          leaders={leaders}
        />

        <div className="team-additional">
          <div className="section-container">
            <div className="team-content">
              <div className="eyebrow centered">Culture</div>
              <h2>Our Collaborative Approach</h2>
              <div className="approach-row">
                <div className="approach-item">
                  <h3>Diverse Expertise</h3>
                  <p>Our team brings together complementary skills in quantitative finance, fundamental analysis, and technology development to create a comprehensive investment approach.</p>
                </div>
                <div className="approach-item">
                  <h3>Continuous Learning</h3>
                  <p>We maintain our competitive edge through ongoing research, market analysis, and refinement of our systematic investment processes.</p>
                </div>
                <div className="approach-item">
                  <h3>Disciplined Execution</h3>
                  <p>Every team member is committed to maintaining the highest standards of analytical rigor and investment discipline that define our approach.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CONTACT SECTION ===== */}
      <section id="contact" className="page-section">
        <div className="contact-content">
          <div className="section-container">
            <div className="contact-info-only">
              <div className="contact-info">
                <div className="eyebrow centered">Contact</div>
                <h2>Get In Touch</h2>
                <p className="contact-description">
                  Ready to explore how Third Locust's systematic investment approach can benefit your portfolio?
                  Our team is here to answer your questions and discuss potential opportunities.
                </p>

                <div className="contact-details">
                  <div className="contact-detail">
                    <h3>Email</h3>
                    <p>info@thirdlocust.com</p>
                  </div>

                  <div className="contact-detail">
                    <h3>Address</h3>
                    <p>Denver, CO</p>
                  </div>

                  <div className="contact-detail">
                    <h3>Business Hours</h3>
                    <p>Mon&ndash;Fri: 9:00 AM&ndash;5:00 PM MT<br />
                       Sat&ndash;Sun: Closed</p>
                  </div>
                </div>

                <div className="investment-note">
                  <h3>Investment Eligibility</h3>
                  <p>
                    Third Locust Capital Management is available to accredited investors only.
                    Please ensure you meet the necessary qualifications before submitting an inquiry.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="faq-section">
          <div className="section-container">
            <div className="faq-content">
              <div className="eyebrow centered">Questions</div>
              <h2>Frequently Asked Questions</h2>
              <p className="faq-intro">
                Here are answers to some common questions about Third Locust's investment approach and philosophy.
              </p>

              <div className="faq-list">
                {faqs.map((faq, index) => (
                  <details key={index} className="faq-item" open={index === 0}>
                    <summary>
                      {faq.question}
                      <span className="faq-plus">+</span>
                    </summary>
                    <p>{faq.answer}</p>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MainPage;
